import { AlertTriangle } from 'lucide-preact'

/**
 * What QA still owes, as one small chip for a list row (ORB-889).
 *
 * ⚑ Exists because the console is switched off outside dev, so ORB-887's queue row — the surface
 * that answers "where is QA work waiting" — cannot be reached in stage or production. Without
 * something on the lists people already read, a finding is invisible unless somebody opens the exact
 * asset version carrying it.
 *
 * ⚑⚑ <b>This chip counts work owed, and nothing else.</b> An unverifiable check is not work owed:
 * there is no override on it and no button to press — the platform is asking someone to go and look
 * with their own eyes. Putting that on a list row as a number made it read as a queue of tasks, which
 * is how "42 to confirm" came to sit beside an asset whose findings had all been settled.
 *
 * ⚑ So the two are not merely kept apart, they are not both here. A finding is something wrong and
 * belongs on a list; due diligence belongs in the panel, where the check says what could not be
 * confirmed and why. Decided with Joel on 2026-10-01 (ORB-987).
 *
 * ⚑ What is NOT lost: the verdict pill still caps at "Verified where possible" when a core check
 * could not be performed, so a run never claims a clean pass on something it did not see. That is
 * what <c>IsCore</c> now decides, and the only thing it decides.
 *
 * Renders nothing when nothing is owed. No green tick: the read behind this only returns work that is
 * owed, so "clean" and "never checked" arrive identically and drawing a tick would assert something
 * neither of them says.
 */
interface Props {
    openFindings: number
    criticalFindings: number
    /**
     * Prefixes the label on a row that stands for a web rather than a single asset.
     *
     * ⚑ Must be assets with FINDINGS — the same kind of thing `openFindings` counts, so the two can
     * share one sentence. Passing every asset the QA read returned put an unverifiable one in the
     * prefix of a fix count: "3 assets · 1 to fix". Raised by Copilot on PR 244.
     */
    assetCount?: number
}

export function AssetQaChip({ openFindings, criticalFindings, assetCount }: Props) {
    if (openFindings === 0) return null

    // Critical is the word that decides whether this is read now rather than later, so it takes the
    // stronger treatment. Amber otherwise — the same tone the sign-off readiness chip uses for "this
    // needs you", which is the signal people here already know how to read.
    const tone = criticalFindings > 0
        ? 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/20 dark:text-rose-300 dark:border-rose-800'
        : 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800'

    const Icon = AlertTriangle

    return (
        <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${tone}`}
            title={describe(openFindings, criticalFindings, assetCount)}
        >
            <Icon className="w-3 h-3 flex-shrink-0" />
            {label(openFindings, criticalFindings, assetCount)}
        </span>
    )
}

/**
 * The chip's own words. Short enough for a list row, and it never says "3" where the three things
 * are of two different kinds.
 */
function label(
    openFindings: number,
    criticalFindings: number,
    assetCount?: number,
): string {
    const prefix = assetCount && assetCount > 1 ? `${assetCount} assets · ` : ''

    // Criticals are worth naming on the chip rather than only in the tooltip — a reader scanning a
    // list is deciding what to open first, and that is the fact that decides it.
    return criticalFindings > 0
        ? `${prefix}${openFindings} to fix · ${criticalFindings} critical`
        : `${prefix}${openFindings} to fix`
}

/** The hover text, which has room to separate the two kinds properly. */
function describe(
    openFindings: number,
    criticalFindings: number,
    assetCount?: number,
): string {
    const parts: string[] = []

    if (assetCount && assetCount > 1) parts.push(`${assetCount} assets with findings`)

    parts.push(criticalFindings > 0
        ? `${openFindings} open ${openFindings === 1 ? 'finding' : 'findings'}, ${criticalFindings} of them Critical`
        : `${openFindings} open ${openFindings === 1 ? 'finding' : 'findings'}`)

    return parts.join(' · ')
}
